/**
 * One card for everything that marks a patch group.
 *
 * Two sections: the connected laser (remote marking through the local laser
 * agent, present only while an agent answers) and the marking file for the
 * laser software. The predicted marking time belongs to the file, not to the
 * way it reaches the laser, so it is shown once, in the card header.
 */
import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import MarkingTimeChip from "./MarkingTimeChip";

/** Small caps label above a section of the card. */
export function MarkingSectionLabel({ children, sx }) {
    return (
        <Typography
            variant="overline"
            component="div"
            sx={{
                lineHeight: 1.6,
                letterSpacing: "0.08em",
                fontWeight: 700,
                color: "text.secondary",
                mb: 0.5,
                ...sx,
            }}
        >
            {children}
        </Typography>
    );
}

/**
 * Props:
 *   title: card heading ("Marking").
 *   predictedMarkingTime: the backend's {seconds, text} payload or seconds;
 *       shown once in the header, nothing without a value.
 *   remote: the page's LaserMarkPanel (with sectionTitle), rendered above the
 *       file section; it renders nothing without an agent, so the file
 *       section then stands alone.
 *   children: the file section's controls (download, preview).
 */
export default function MarkingOptionsBox({
    title = "Marking",
    predictedMarkingTime = null,
    remote = null,
    children,
    sx,
}) {
    return (
        <Paper
            variant="outlined"
            data-testid="marking-options-box"
            sx={{
                width: "100%",
                maxWidth: 760,
                boxSizing: "border-box",
                p: { xs: 2, md: 2.5 },
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
                ...sx,
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ gap: 1, mb: 1.5 }}
            >
                <Typography variant="subtitle2" fontWeight={700}>
                    {title}
                </Typography>
                <MarkingTimeChip value={predictedMarkingTime} />
            </Stack>
            {remote}
            <Box data-testid="marking-file-section">
                <MarkingSectionLabel>Marking file</MarkingSectionLabel>
                {children}
            </Box>
        </Paper>
    );
}
